import { BreadCrumb } from "primereact/breadcrumb";
import { Card } from "primereact/card";
import type { MenuItem } from "primereact/menuitem";
import Table from "../components/table";
import { TABLE_COLUMNS_CONFIG, type ColumnMeta } from "../constants/table.cnst";
import { useCallback, useState } from "react";
import { Tag } from "primereact/tag";
import { fetchApi } from "../services/http.service";
import { APIMapping } from "../constants/api-mapping.cnst";
import { HBL_PA_RESOURCES } from "../constants/data.cnst";
import { useQuery } from "@tanstack/react-query";
import type { LocoHealthNew, StationHealthNew, ZoneOption } from "../interfaces/dashboard"
import { MultiSelect } from "primereact/multiselect";
import { DEFAULT_ROUTE_PRIORITY } from "../constants/defaultRoutes";
import { useNavigate } from "react-router-dom";

export default function RadioHealth() {

    const breadcrumb: MenuItem[] = [
        { label: 'Health', style: { pointerEvents: 'none', fontWeight: 500 } },
        { label: 'Radio Health - RF Issues', style: { pointerEvents: 'none' }, className: 'font-semibold text-gray-900' }
    ];
    const radioHealthLocoRFIssuesTableColumns: ColumnMeta[] = TABLE_COLUMNS_CONFIG.RADIO_HEALTH_LOCO_RF_ISSUES;
    const radioHealthStationRFIssuesTableColumns: ColumnMeta[] = TABLE_COLUMNS_CONFIG.RADIO_HEALTH_STATION_RF_ISSUES;

    const [radioHealthLocoRFIssuesQueryParams, setRadioHealthLocoRFIssuesQueryParams] = useState({
        total_entry_per_page: 10,
        page_num: 1,
    });
    const navigate = useNavigate();

    const [selectedLocos, setSelectedLocos] = useState([]);
    const [expandLocoRows, setExpandLocoRows] = useState(true);
    const [locoExpansionRequest, setLocoExpansionRequest] = useState(0);
    const [allLocoRowsExpanded, setAllLocoRowsExpanded] = useState(true);
    const [allLocoRowsCollapsed, setAllLocoRowsCollapsed] = useState(false);
    const handleLocoExpansionStateChange = useCallback((allExpanded: boolean, allCollapsed: boolean) => {
        setAllLocoRowsExpanded(allExpanded);
        setAllLocoRowsCollapsed(allCollapsed);
    }, []);
    const { data: locoListResponseData } = useQuery({
        queryKey: ["locolist-radio-health"],
        queryFn: () =>
            fetchApi({
                endpoint: APIMapping.GET_LOCOS_IN_RADIO_HEALTH,
                resourceName: HBL_PA_RESOURCES.RADIO_HEALTH
            }),
        enabled: true
    });

    const locoOptions =
        (locoListResponseData?.response ?? []).map((id: any) => ({
            label: id.toString(),
            value: id.toString(),
        }));


    const { data: locoRFIssueResponse, isFetching: isLocoRFIssueFetching } = useQuery<any>({
        queryKey: ["loco-rf-issue-radio", radioHealthLocoRFIssuesQueryParams, selectedLocos],
        queryFn: () =>
            fetchApi({
                endpoint: APIMapping.GET_LOCO_RF_ISSUES_RADIO_HEALTH,
                queryParams: { ...radioHealthLocoRFIssuesQueryParams, locoId: selectedLocos.join(',') },
                resourceName: HBL_PA_RESOURCES.RADIO_HEALTH
            }),
        enabled: true
    });

    const [firstForLocoRFIssueTable, setFirstForLocoRFIssueTable] = useState(0);

    const locoRFIssuesTableData: LocoHealthNew[] =
        locoRFIssueResponse?.response?.locos?.map(
            (rowData: LocoHealthNew) => {
                return {
                    ...rowData,
                    failedHealthModules: rowData.failedModules?.map(
                        (module) =>
                            `${module.category.replaceAll('_', ' ')} ${module.count}`
                    ) ?? [],
                };
            }
        ) ?? [];
    const [rowsForLocoRFIssueTable, setRowsForLocoRFIssueTable] = useState(10);
    const totalEventsForLocoRFIssueTable = locoRFIssueResponse?.response?.totalRecords ?? 0;


    const buildFinalPath = (data: any, healthModule: any, key: string) => {
        const params = new URLSearchParams(location.search);

        if(key === 'loco-health') params.set("locoId", data.locoId);
        else params.set("stationId", `${data.stationId} (${data.stationName})`);
        params.set("fromDate", data['date']);
        params.set("toDate", data['date']);

        const path = DEFAULT_ROUTE_PRIORITY.find(r => r.name === healthModule && r.path.includes(key));

        navigate(`${path?.path}?${params.toString()}`);
    };

    const renderLocoHealthExpansion = (
        rowData: LocoHealthNew
    ) => {
        const locoData = rowData;

        return (
            <div className="p-3">

                <Table
                    id="loco-health-expansion"
                    columns={TABLE_COLUMNS_CONFIG.LOCO_HEALTH_EXPANSION}
                    data={rowData.failedModules ?? []}
                    showGridlines={true}
                    paginator={false}
                    first={0}
                    isHighlight={true}
                    rows={rowData.failedModules?.length ?? 0}
                    enableScroll={false}
                    renderCell={(rowData, field) => {

                        if (field === 'failureDetails') {
                            return (
                                <div className="flex flex-wrap gap-2">
                                    {rowData.failureDetails?.map((issue: any) => {

                                        return (
                                            <Tag
                                                key={issue}
                                                value={issue}
                                                severity="info"
                                            />
                                        );
                                    })}
                                </div>
                            );
                        }

                        if (field === 'moduleName') {
                            return (
                                <a id={`row-${field}`} className="underline cursor-pointer text-brand-600 dark:text-brand-50" onClick={() => buildFinalPath(locoData, rowData[field], 'loco-health')}>
                                    {rowData[field]}
                                </a>
                            );
                        }

                        return rowData[field];
                    }}
                />

            </div>
        );
    };


    const [stationRFIssuesQueryParams, setStationRFIssuesQueryParams] = useState({
        total_entry_per_page: 10,
        page_num: 1,
    });


    const [selectedZones, setSelectedZones] = useState<string[]>([]);
    const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
    const [selectedStations, setSelectedStations] = useState<string[]>([]);
    const [expandStationRows, setExpandStationRows] = useState(true);
    const [stationExpansionRequest, setStationExpansionRequest] = useState(0);
    const [allStationRowsExpanded, setAllStationRowsExpanded] = useState(true);
    const [allStationRowsCollapsed, setAllStationRowsCollapsed] = useState(false);
    const handleStationExpansionStateChange = useCallback((allExpanded: boolean, allCollapsed: boolean) => {
        setAllStationRowsExpanded(allExpanded);
        setAllStationRowsCollapsed(allCollapsed);
    }, []);
    const { data: filtersResponseData } = useQuery({
        queryKey: ["staion-rf-issues-filter"],
        queryFn: () =>
            fetchApi({
                endpoint: APIMapping.GET_ZONES_PROJECT_STATION_IN_RADIO_HEALTH,
                resourceName: HBL_PA_RESOURCES.RADIO_HEALTH
            }),
        enabled: true
    });

    const zoneOptions: ZoneOption[] = (
        filtersResponseData?.response ?? []
    ).map((zone: any) => ({
        label: zone.zone,
        value: zone.zone,
        projects: zone.projects ?? []
    }));

    const projectOptions = (
        selectedZones.length
            ? zoneOptions.filter((zone) =>
                selectedZones.includes(zone.value)
            )
            : zoneOptions
    ).flatMap((zone) =>
        zone.projects.map((project: any) => ({
            label: project.project,
            value: project.project,
            stations: project.stations ?? []
        }))
    );

    const stationOptions = projectOptions
        .filter((project: any) =>
            selectedProjects.length
                ? selectedProjects.includes(project.value)
                : true
        )
        .flatMap((project: any) =>
            project.stations.map((station: any) => ({
                label: `${station.stationId} (${station.stationName})`,
                value: `${station.stationId} (${station.stationName})`
            }))
        );

    const { data: eventsApiResponse, isFetching } = useQuery<any>({
        queryKey: ["station-rf-issue-table", stationRFIssuesQueryParams, selectedStations, selectedZones, selectedProjects],
        queryFn: () =>
            fetchApi({
                endpoint: APIMapping.GET_STATION_RF_ISSUES_RADIO_HEALTH,
                queryParams: { ...stationRFIssuesQueryParams, stationId: selectedStations.map((item: any) => item.split(' (')[0]).join(","), zone: selectedZones.join(","), project: selectedProjects.join(",") },
                resourceName: HBL_PA_RESOURCES.STATION_HEALTH
            }),
        enabled: true
    });

    const [firstForStationRFIssueTable, setFirstForStationRFIssueTable] = useState(0);
    const stationHealthTableData: StationHealthNew[] =
        eventsApiResponse?.response?.stations?.map((rowData: StationHealthNew) => {
            return {
                ...rowData,
                failedHealthModules: rowData.failedModules?.map(
                    (module) =>
                        `${module.category.replaceAll('_', ' ')} ${module.count}`
                ) ?? [],
            };
        }) ?? [];
    const [rowsForStationRFIssueTable, setRowsForStationRFIssueTable] = useState(10);
    const totalEventsForStationRFIssueTable = eventsApiResponse?.response?.totalRecords ?? 0;


    const renderStationHealthExpansion = (
        rowData: StationHealthNew
    ) => {
        const stationData = rowData;
        return (
            <div className="p-3">

                <Table
                    id="loco-health-expansion"
                    columns={TABLE_COLUMNS_CONFIG.LOCO_HEALTH_EXPANSION}
                    data={rowData.failedModules ?? []}
                    showGridlines={true}
                    paginator={false}
                    first={0}
                    isHighlight={true}
                    rows={rowData.failedModules?.length ?? 0}
                    enableScroll={false}

                    renderCell={(rowData, field) => {

                        if (field === 'failureDetails') {
                            return (
                                <div className="flex flex-wrap gap-2">
                                    {rowData.failureDetails?.map((issue: any) => {

                                        return (
                                            <Tag
                                                key={issue}
                                                value={issue}
                                                severity="info"
                                            />
                                        );
                                    })}
                                </div>
                            );
                        }

                        if (field === 'moduleName') {
                            return (
                                <a id={`row-${field}`} className="underline cursor-pointer text-brand-600 dark:text-brand-50" onClick={() => buildFinalPath(stationData, rowData[field], 'station-health')}>
                                    {rowData[field]}
                                </a>
                            );
                        }

                        return rowData[field];
                    }}
                />

            </div>
        );
    };

    const locoRFIssuesHeader = (

        <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
                <h3 className="m-0 font-semibold">Loco RF Issues</h3>
                <button type="button" aria-label="Expand all loco rows" title="Expand all rows" disabled={allLocoRowsExpanded} className="p-2 hover:bg-gray-100 rounded cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent" onClick={() => { setExpandLocoRows(true); setLocoExpansionRequest((request) => request + 1); }}>
                    <i className="pi pi-plus-circle" />
                </button>
                <button type="button" aria-label="Collapse all loco rows" title="Collapse all rows" disabled={allLocoRowsCollapsed} className="p-2 hover:bg-gray-100 rounded cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent" onClick={() => { setExpandLocoRows(false); setLocoExpansionRequest((request) => request + 1); }}>
                    <i className="pi pi-minus-circle" />
                </button>
            </div>

            <MultiSelect
                value={selectedLocos}
                options={locoOptions ?? []} // [{ label, value }]
                filter
                resetFilterOnHide
                onChange={(e: any) => {
                    const selected = e.value;
                    setSelectedLocos(selected);

                    setFirstForLocoRFIssueTable(0);
                    setRowsForLocoRFIssueTable(10);
                    setRadioHealthLocoRFIssuesQueryParams((prev: any) => ({
                        ...prev,
                        page_num: 1,
                        total_entry_per_page: 10,
                    }));
                }}
                placeholder="Select Locos"
                className="w-80"
                display="chip"
            />
        </div>

    );


    const stationRFIssuesHeader = (

        <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
                <h3 className="m-0 font-semibold">Station RF Issues</h3>
                <button type="button" aria-label="Expand all station rows" title="Expand all rows" disabled={allStationRowsExpanded} className="p-2 hover:bg-gray-100 rounded cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent" onClick={() => { setExpandStationRows(true); setStationExpansionRequest((request) => request + 1); }}>
                    <i className="pi pi-plus-circle" />
                </button>
                <button type="button" aria-label="Collapse all station rows" title="Collapse all rows" disabled={allStationRowsCollapsed} className="p-2 hover:bg-gray-100 rounded cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent" onClick={() => { setExpandStationRows(false); setStationExpansionRequest((request) => request + 1); }}>
                    <i className="pi pi-minus-circle" />
                </button>
            </div>


            <div className="flex justify-end gap-2">
                <MultiSelect
                    value={selectedZones}
                    options={zoneOptions ?? []} // [{ label, value }]
                    filter
                    resetFilterOnHide
                    onChange={(e: any) => {
                        const selected = e.value;
                        setSelectedZones(selected);
                        setSelectedProjects([]);
                        setSelectedStations([]);

                        setFirstForStationRFIssueTable(0);
                        setRowsForStationRFIssueTable(10);
                        setStationRFIssuesQueryParams((prev: any) => ({ ...prev, page_num: 1, total_entry_per_page: 10 }));
                    }}
                    placeholder="Select Zones"
                    className="w-56"
                    display="chip"
                />

                <MultiSelect
                    value={selectedProjects}
                    options={projectOptions ?? []} // [{ label, value }]
                    filter
                    resetFilterOnHide
                    onChange={(e: any) => {
                        const selected = e.value;
                        setSelectedProjects(selected);
                        setSelectedStations([]);

                        setFirstForStationRFIssueTable(0);
                        setRowsForStationRFIssueTable(10);
                        setStationRFIssuesQueryParams((prev: any) => ({ ...prev, page_num: 1, total_entry_per_page: 10 }));
                    }}
                    placeholder="Select Projects"
                    className="w-56"
                    display="chip"
                />

                <MultiSelect
                    value={selectedStations}
                    options={stationOptions ?? []} // [{ label, value }]
                    filter
                    resetFilterOnHide
                    onChange={(e: any) => {
                        const selected = e.value;
                        setSelectedStations(selected);

                        setFirstForStationRFIssueTable(0);
                        setRowsForStationRFIssueTable(10);
                        setStationRFIssuesQueryParams((prev: any) => ({ ...prev, page_num: 1, total_entry_per_page: 10 }));
                    }}
                    placeholder="Select Stations"
                    className="w-74"
                    display="chip"
                />
            </div>
        </div>

    );




    return (
        <div className="h-[calc(100vh-4rem)] p-6 flex-1 overflow-y-auto">

            <BreadCrumb model={breadcrumb} />
            <div className="grid grid-cols-1 mb-6 pt-4">

                <Card header={locoRFIssuesHeader}>

                    <Table
                        id="loco-health"
                        columns={radioHealthLocoRFIssuesTableColumns}
                        data={locoRFIssuesTableData}
                        showGridlines={true}
                        paginator={true}
                        isLoading={isLocoRFIssueFetching}
                        first={firstForLocoRFIssueTable}
                        rows={rowsForLocoRFIssueTable}
                        totalRecords={totalEventsForLocoRFIssueTable}
                        renderCell={(rowData, field, rowIndex, path, expansion) => {
                            if (field === 'serial') {
                                console.log(path)
                                return firstForLocoRFIssueTable + rowIndex + 1;
                            }

                            if (field === "action") {
                                return (
                                    <button
                                        type="button"
                                        className="p-2 hover:bg-gray-100 rounded cursor-pointer"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            expansion?.toggle();
                                        }}
                                    >
                                        <i
                                            className={
                                                expansion?.isExpanded
                                                    ? "pi pi-chevron-up"
                                                    : "pi pi-chevron-down"
                                            }
                                        />
                                    </button>
                                );
                            }

                            return rowData[field];
                        }}
                        rowExpansion={{
                            key: 'locoId',
                            render: renderLocoHealthExpansion
                        }}
                        expandAllRows={expandLocoRows}
                        expansionRequest={locoExpansionRequest}
                        onExpansionStateChange={handleLocoExpansionStateChange}
                        onPageChangeExternal={(page_num, total_entry_per_page) => {
                            setFirstForLocoRFIssueTable((page_num - 1) * total_entry_per_page);
                            setRowsForLocoRFIssueTable(total_entry_per_page);
                            setRadioHealthLocoRFIssuesQueryParams((prev: any) => ({
                                ...prev,
                                page_num: page_num,
                                total_entry_per_page: total_entry_per_page
                            }));


                        }}
                    />
                </Card>
            </div>


            <div className="grid grid-cols-1 mb-6 pt-4">

                <Card header={stationRFIssuesHeader}>

                    <Table
                        columns={radioHealthStationRFIssuesTableColumns}
                        data={stationHealthTableData}
                        showGridlines={true}
                        paginator={true}
                        isLoading={isFetching}
                        first={firstForStationRFIssueTable}
                        rows={rowsForStationRFIssueTable}
                        totalRecords={totalEventsForStationRFIssueTable}
                        renderCell={(rowData, field, rowIndex, path, expansion) => {

                            if (field === 'serial') {
                                console.log(path)
                                return firstForStationRFIssueTable + rowIndex + 1;
                            }

                            if (field === "action") {
                                return (
                                    <button
                                        type="button"
                                        className="p-2 hover:bg-gray-100 rounded cursor-pointer"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            expansion?.toggle();
                                        }}
                                    >
                                        <i
                                            className={
                                                expansion?.isExpanded
                                                    ? "pi pi-chevron-up"
                                                    : "pi pi-chevron-down"
                                            }
                                        />
                                    </button>
                                );
                            }


                            return rowData[field];
                        }}
                        rowExpansion={{
                            key: 'stationId',
                            render: renderStationHealthExpansion
                        }}
                        expandAllRows={expandStationRows}
                        expansionRequest={stationExpansionRequest}
                        onExpansionStateChange={handleStationExpansionStateChange}
                        onPageChangeExternal={(page_num, total_entry_per_page) => {
                            setFirstForStationRFIssueTable((page_num - 1) * total_entry_per_page);
                            setRowsForStationRFIssueTable(total_entry_per_page);
                            setStationRFIssuesQueryParams((prev: any) => ({
                                ...prev,
                                page_num: page_num,
                                total_entry_per_page: total_entry_per_page
                            }));


                        }}
                    />
                </Card>
            </div>
        </div>
    );

}